import { DefaultAgentIcon } from 'ai-components-ui-library/components/icons/internal/DefaultAgentIcon';
import { getAppInfo } from 'chatspot-core/utils/appUtils';
import { DocumentsIcon } from 'foundations-assets/icons/components/DocumentsIcon';
import { FileIcon } from 'foundations-assets/icons/components/FileIcon';
import { PdfFileIcon } from 'foundations-assets/icons/components/PdfFileIcon';
import { UserPersonalizationIcon } from 'foundations-assets/icons/components/UserPersonalizationIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Image } from 'foundations-components/transitional/components/Image';
import { gate } from 'hub-http/gates';
import Raven from 'raven-js';
import { useContext } from 'react';
import { HUBSPOT_WEB_CITATION_ICON_MAP } from '../../constants/icons';
import { SectionContext } from '../../context/SectionContext';
import { getCrmCitationObjectNameAndIcon } from '../../utils/citationUtils';
import { sanitizeImageSrc } from '../../utils/imageSrcSanitizer';
import CrmCitationIcon from '../text/MarkdownWithCitations/CrmCitationIcon';

// Not intended for use outside ai-rendering-library. Prefer composing with CitationsIcon instead.
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledIconHolderWrapper = ({
  Icon,
  size = 20
}) => /*#__PURE__*/_jsx(Flex, {
  backgroundColor: "icon-core-default",
  borderRadius: "full",
  blockSize: `${size}px`,
  inlineSize: `${size}px`,
  padding: "100",
  alignItems: "center",
  justifyContent: "center",
  children: /*#__PURE__*/_jsx(Icon, {
    color: "icon-core-on-fill-default"
  })
});
// Not intended for use outside ai-rendering-library.
export const CitationsIcon = ({
  iconSrc,
  sourceType,
  data,
  size = 18
}) => {
  const {
    getIsUngated,
    allowedImageHosts
  } = useContext(SectionContext);
  const isUngatedForLocationWithinSource = getIsUngated(gate('KnowledgeBox:LocationWithinSource'));
  if (iconSrc) {
    const sanitizedSrc = sanitizeImageSrc(iconSrc, {
      trustedHosts: allowedImageHosts
    });
    if (!sanitizedSrc) {
      Raven.captureMessage('CitationsIcon: Invalid image src', {
        extra: {
          src: iconSrc
        }
      });
      return null;
    }
    return /*#__PURE__*/_jsx(Image, {
      src: iconSrc,
      alt: iconSrc,
      width: size,
      height: size,
      shape: "rounded"
    });
  }
  if (sourceType === 'CRM_OBJECT' && 'objectTypeId' in data) {
    const {
      icon: IconToUse
    } = getCrmCitationObjectNameAndIcon({
      objectTypeId: data.objectTypeId
    });
    const crmSize = size >= 32 ? '600' : isUngatedForLocationWithinSource ? '400' : '50';
    const crmPadding = size >= 32 ? '200' : isUngatedForLocationWithinSource ? '100' : '50';
    return /*#__PURE__*/_jsx(CrmCitationIcon, {
      crmSize: size,
      size: crmSize,
      padding: crmPadding,
      defaultIcon: IconToUse,
      objectTypeId: data.objectTypeId,
      objectId: data.objectId
    });
  }
  if (sourceType === 'APP') {
    const appSourceData = data;
    const appInfo = getAppInfo(appSourceData.appName, appSourceData.url);
    return /*#__PURE__*/_jsx(Image, {
      src: appInfo.iconUrl,
      alt: appInfo.displayName,
      width: size,
      height: size,
      shape: "rounded"
    });
  }
  if (sourceType === 'WEBSITE' && 'domain' in data) {
    if ('url' in data && (data.domain === 'hubspot.com' || data.domain.endsWith('.hubspot.com') || data.domain === 'hubspotqa.com' || data.domain.endsWith('.hubspotqa.com'))) {
      const matchedIcon = HUBSPOT_WEB_CITATION_ICON_MAP.find(({
        slug
      }) => {
        try {
          const url = new URL(data.url);
          return url.pathname.startsWith(`/${slug}/`);
        } catch (_unused) {
          return data.url.includes(`/${slug}/`);
        }
      });
      if (matchedIcon) {
        return /*#__PURE__*/_jsx(StyledIconHolderWrapper, {
          Icon: matchedIcon.icon,
          size: size
        });
      }
    }
    return /*#__PURE__*/_jsx(Image, {
      src: `https://www.google.com/s2/favicons?sz=${size * 2}&domain=${data === null || data === void 0 ? void 0 : data.domain}`,
      alt: data === null || data === void 0 ? void 0 : data.domain,
      width: size,
      height: size,
      shape: "rounded"
    });
  }
  if (sourceType === 'AI_SKILL') {
    return /*#__PURE__*/_jsx(DefaultAgentIcon, {
      marginBlockEnd: "0",
      size: size,
      rounded: true
    });
  }
  if (sourceType === 'BRAND_IDENTITY') {
    return /*#__PURE__*/_jsx(StyledIconHolderWrapper, {
      Icon: UserPersonalizationIcon,
      size: size
    });
  }
  if (sourceType === 'FILE_UPLOAD' && 'fileType' in data) {
    switch (data === null || data === void 0 ? void 0 : data.fileType) {
      case 'pdf':
        return /*#__PURE__*/_jsx(StyledIconHolderWrapper, {
          Icon: PdfFileIcon,
          size: size
        });
      default:
        return /*#__PURE__*/_jsx(StyledIconHolderWrapper, {
          Icon: FileIcon,
          size: size
        });
    }
  }
  return /*#__PURE__*/_jsx(StyledIconHolderWrapper, {
    Icon: DocumentsIcon,
    size: size
  });
};